import {useQuery} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    GetMoneyPageTriggers as GetMoneyPageTriggersQuery,
    GetMoneyPageTriggers_maUrlTriggers_edges_node as MoneyPageTrigger,
    GetMoneyPageTriggersVariables,
} from 'nutshell-graphql-types';

import {nutApolloClient} from '../../../../../apollo-client';

import GET_MONEY_PAGE_TRIGGERS from '../queries/get-money-page-triggers.graphql';

export const GET_MONEY_PAGE_TRIGGERS_LIMIT = 50;

export const useGetMoneyPageTriggers = (
    maUrlId: string
): {
    triggers: MoneyPageTrigger[];
    isLoading: boolean;
    error: string | null | undefined;
    refetch: () => Promise<any>;
    totalCount: number;
    hasNextPage: boolean;
    fetchNextPage: () => Promise<any>;
    fetchPreviousPage: () => Promise<any>;
} => {
    const variables: GetMoneyPageTriggersVariables = {
        maUrlId,
        first: GET_MONEY_PAGE_TRIGGERS_LIMIT,
    };

    const {loading, data, error, refetch, fetchMore} = useQuery<
        GetMoneyPageTriggersQuery,
        GetMoneyPageTriggersVariables
    >(GET_MONEY_PAGE_TRIGGERS, {
        variables,
        fetchPolicy: 'cache-and-network',
    });

    const pageInfo = data && data.maUrlTriggers && data.maUrlTriggers.pageInfo;
    const hasNextPage = pageInfo && pageInfo.hasNextPage;
    const hasPreviousPage = pageInfo && pageInfo.hasPreviousPage;
    const endCursor = pageInfo && pageInfo.endCursor;
    const startCursor = pageInfo && pageInfo.startCursor;
    const totalCount = data && data.maUrlTriggers ? data.maUrlTriggers.totalCount : 0;

    const triggers =
        data && data.maUrlTriggers && data.maUrlTriggers.edges
            ? data.maUrlTriggers.edges.map((edge: {node: MoneyPageTrigger}) => edge.node)
            : [];

    const fetchPage = (direction: 'next' | 'previous') => {
        const isNext = direction === 'next';
        const canFetch = isNext ? hasNextPage : hasPreviousPage;

        if (!canFetch) {
            return Promise.resolve();
        }

        const pageVariables: GetMoneyPageTriggersVariables = isNext
            ? {
                  maUrlId,
                  first: GET_MONEY_PAGE_TRIGGERS_LIMIT,
                  after: endCursor,
                  last: null,
                  before: null,
              }
            : {
                  maUrlId,
                  last: GET_MONEY_PAGE_TRIGGERS_LIMIT,
                  before: startCursor,
                  first: null,
                  after: null,
              };

        return fetchMore({
            variables: pageVariables,
            updateQuery: (prev: GetMoneyPageTriggersQuery, {fetchMoreResult}) => {
                if (!fetchMoreResult) return prev;

                nutApolloClient.writeQuery({
                    query: GET_MONEY_PAGE_TRIGGERS,
                    variables: pageVariables,
                    data: fetchMoreResult,
                });

                return fetchMoreResult;
            },
        });
    };

    return {
        triggers,
        isLoading: loading,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
        refetch,
        totalCount,
        hasNextPage: hasNextPage || false,
        fetchNextPage: () => fetchPage('next'),
        fetchPreviousPage: () => fetchPage('previous'),
    };
};
